/**
 * ValidationContent - Content renderer for Validation panel
 * 
 * Provides validation tools for document framework compliance.
 * Streamlined interface for quick validation runs.
 */

import { useState } from 'react';
import { Button } from '@/components/ui';
import type { ValidationResult } from '../../types';

type ValidationType = 'metadata' | 'links' | 'crosswalk' | 'all';

interface ValidationContentProps {
  metadataResult: ValidationResult | null;
  linksResult: ValidationResult | null;
  crosswalkResult: ValidationResult | null;
  allResult: ValidationResult | null;
  validating: boolean;
  error: string | null;
  onRunValidation: (type: ValidationType) => void;
}

export function ValidationContent({
  metadataResult,
  linksResult,
  crosswalkResult,
  allResult,
  validating,
  error,
  onRunValidation,
}: ValidationContentProps) {
  const [selectedType, setSelectedType] = useState<ValidationType>('all');
  
  // Get overall status
  const getOverallStatus = () => {
    if (!metadataResult && !linksResult && !crosswalkResult && !allResult) {
      return 'pending';
    }
    
    const results = [metadataResult, linksResult, crosswalkResult];
    const hasFailures = results.some(r => r && !r.passed);
    const hasWarnings = results.some(r => r && r.warnings && r.warnings.length > 0);
    
    if (hasFailures) return 'failed';
    if (hasWarnings) return 'warning';
    return 'passed';
  };
  
  const overallStatus = getOverallStatus();
  
  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header - matches DocumentViewerContent styling */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-background">
        <h2 className="text-sm font-medium text-foreground flex-1">
          Validation Suite
        </h2>
      </div>
      
      {/* Error display */}
      {error && (
        <div className="flex-shrink-0 mx-4 mt-3 p-3 bg-destructive-light border border-destructive rounded-lg">
          <p className="text-sm text-destructive">
            <strong>Error:</strong> {error}
          </p>
        </div>
      )}
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Quick Actions */}
        <div className="border border-border rounded-lg p-4">
          <h4 className="text-sm font-semibold text-foreground mb-3">
            Run Validation Checks
          </h4>
          
          <div className="grid grid-cols-2 gap-2 mb-4">
            <ValidationButton
              label="Metadata"
              icon="📋"
              description="Check document metadata"
              isSelected={selectedType === 'metadata'}
              onClick={() => setSelectedType('metadata')}
              result={metadataResult}
            />
            <ValidationButton
              label="Links"
              icon="🔗"
              description="Verify document links"
              isSelected={selectedType === 'links'}
              onClick={() => setSelectedType('links')}
              result={linksResult}
            />
            <ValidationButton
              label="Crosswalk"
              icon="🔀"
              description="Check standards mapping"
              isSelected={selectedType === 'crosswalk'}
              onClick={() => setSelectedType('crosswalk')}
              result={crosswalkResult}
            />
            <ValidationButton
              label="All Checks"
              icon="✓"
              description="Run complete suite"
              isSelected={selectedType === 'all'}
              onClick={() => setSelectedType('all')}
              result={allResult ? { passed: allResult.passed !== false, errors: [], warnings: [] } : null}
            />
          </div>
          
          <Button
            onClick={() => onRunValidation(selectedType)}
            disabled={validating}
            className="w-full"
          >
            {validating ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin">⏳</span>
                Running...
              </span>
            ) : (
              `Run ${selectedType === 'all' ? 'All Checks' : selectedType.charAt(0).toUpperCase() + selectedType.slice(1) + ' Check'}`
            )}
          </Button>
        </div>
        
        {/* Results */}
        {(metadataResult || linksResult || crosswalkResult || allResult) && (
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="bg-muted/30 px-4 py-2 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-foreground">
                Validation Results
              </h4>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                overallStatus === 'passed' ? 'bg-success-light text-success' :
                overallStatus === 'warning' ? 'bg-warning-light text-warning' :
                overallStatus === 'failed' ? 'bg-destructive-light text-destructive' :
                'bg-muted text-muted-foreground'
              }`}>
                {overallStatus === 'passed' ? '✓ All Passed' :
                 overallStatus === 'warning' ? '⚠ Warnings' :
                 overallStatus === 'failed' ? '✗ Failed' :
                 'Not Run'}
              </span>
            </div>
            
            <div className="divide-y divide-gray-100 dark:divide-slate-700">
              {metadataResult && (
                <ValidationResultRow
                  label="Metadata"
                  result={metadataResult}
                />
              )}
              {linksResult && (
                <ValidationResultRow
                  label="Links"
                  result={linksResult}
                />
              )}
              {crosswalkResult && (
                <ValidationResultRow
                  label="Crosswalk"
                  result={crosswalkResult}
                />
              )}
            </div>
          </div>
        )}
        
        {/* Info box */}
        <div className="bg-info-light border border-info rounded-lg p-3">
          <p className="text-xs text-info">
            <strong>Tip:</strong> Run validation checks before releases to ensure 
            document framework compliance with AS9100D and DO-178C standards.
          </p>
        </div>
      </div>
    </div>
  );
}

// Validation button component
interface ValidationButtonProps {
  label: string;
  icon: string;
  description: string;
  isSelected: boolean;
  onClick: () => void;
  result: ValidationResult | null;
}

function ValidationButton({
  label,
  icon,
  description,
  isSelected,
  onClick,
  result,
}: ValidationButtonProps) {
  const getStatusIndicator = () => {
    if (!result) return null;
    if (result.passed) return '✓';
    if (result.warnings && result.warnings.length > 0) return '⚠';
    return '✗';
  };
  
  const getStatusColor = () => {
    if (!result) return '';
    if (result.passed) return 'text-success';
    if (result.warnings && result.warnings.length > 0) return 'text-warning';
    return 'text-destructive';
  };
  
  return (
    <Button
      onClick={onClick}
      variant="outline"
      className={`p-3 h-auto flex-col items-start ${
        isSelected
          ? 'border-primary bg-info-light'
          : ''
      }`}
    >
      <div className="flex items-center justify-between mb-1 w-full">
        <span className="text-lg">{icon}</span>
        {result && (
          <span className={`text-sm ${getStatusColor()}`}>
            {getStatusIndicator()}
          </span>
        )}
      </div>
      <div className="text-sm font-medium text-foreground text-left">
        {label}
      </div>
      <div className="text-xs text-muted-foreground text-left">
        {description}
      </div>
    </Button>
  );
}

// Validation result row component
interface ValidationResultRowProps {
  label: string;
  result: ValidationResult;
}

function ValidationResultRow({ label, result }: ValidationResultRowProps) {
  const [isExpanded, setIsExpanded] = useState(!result.passed);
  
  const hasIssues = (result.errors && result.errors.length > 0) || 
                   (result.warnings && result.warnings.length > 0);
  
  return (
    <div className="px-4 py-2">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => hasIssues && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <span className={`text-sm ${
            result.passed ? 'text-success' : 'text-destructive'
          }`}>
            {result.passed ? '✓' : '✗'}
          </span>
          <span className="text-sm font-medium text-foreground">
            {label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {result.errors && result.errors.length > 0 && (
            <span className="text-xs px-1.5 py-0.5 bg-destructive-light text-destructive rounded">
              {result.errors.length} error{result.errors.length !== 1 ? 's' : ''}
            </span>
          )}
          {result.warnings && result.warnings.length > 0 && (
            <span className="text-xs px-1.5 py-0.5 bg-warning-light text-warning rounded">
              {result.warnings.length} warning{result.warnings.length !== 1 ? 's' : ''}
            </span>
          )}
          {hasIssues && (
            <span className="text-muted-foreground text-xs">
              {isExpanded ? '▼' : '▶'}
            </span>
          )}
        </div>
      </div>
      
      {/* Expanded issues */}
      {isExpanded && hasIssues && (
        <div className="mt-2 pl-6 space-y-1">
          {result.errors && result.errors.map((error, idx) => (
            <div key={idx} className="text-xs text-destructive">
              • {error}
            </div>
          ))}
          {result.warnings && result.warnings.map((warning, idx) => (
            <div key={idx} className="text-xs text-warning">
              • {warning}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

